import type { CollisionDetection, DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core';

import type { ShortlistItem } from '@elara/client-core/models/ShortlistItemModel';

import {
  closestCenter,
  DndContext,
  DragOverlay,
  getFirstCollision,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import React from 'react';

import { isSameShortlistEntry } from '@elara/domain/item-identity';

import { useShortlistState, useShortlistUIState, useTripsState } from '#states/hooks.js';
import { ShortlistItemCard } from '#ui/shortlist/ShortlistItemCard.js';

import { ShortlistItemsGroup } from './ShortlistItemsGroup.js';

export function ShortlistBoardView(): React.ReactNode {
  const tripsState = useTripsState();
  const shortlistState = useShortlistState();
  const uiState = useShortlistUIState();

  const items = shortlistState.items;
  const groups = shortlistState.groups;
  const itemsByGroup = shortlistState.itemsByGroup;

  // Configure sensors for drag and drop
  // On mobile, only use TouchSensor with a delay to prevent accidental drags during scroll.
  // PointerSensor fires on touch devices too (pointer events include touch), so it would
  // bypass the TouchSensor delay and activate with just 8px of movement.
  const mobileSensors = useSensors(
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250, // Require 250ms hold before drag starts
        tolerance: 5, // Allow 5px of movement during delay period
      },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const desktopSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before drag starts
      },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const sensors = tripsState.isMobile ? mobileSensors : desktopSensors;

  const isSortingContainer = uiState.activeId != null ? groups.some((g) => g.id === uiState.activeId) : false;

  // Custom collision detection strategy optimized for multiple containers
  // Based on dnd-kit MultiContainer example to support dropping at end of lists
  const collisionDetectionStrategy: CollisionDetection = React.useCallback(
    (args) => {
      // If dragging a group, only consider other groups as targets
      if (uiState.activeId && groups.some((g) => g.id === uiState.activeId)) {
        return closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter((container) =>
            groups.some((g) => g.id === container.id),
          ),
        });
      }

      // For items: check pointer intersections first (only items the pointer is actually within)
      const pointerIntersections = pointerWithin(args);

      // If pointer is within any items, use those (most accurate)
      if (pointerIntersections.length > 0) {
        const overId = getFirstCollision(pointerIntersections, 'id');
        if (overId != null) {
          uiState.setLastOverId(overId as string);
          return [{ id: overId }];
        }
      }

      // If pointer is not within any items, check rect intersections (includes containers)
      const rectIntersections = rectIntersection(args);
      let overId = getFirstCollision(rectIntersections, 'id');

      if (overId != null) {
        // If over a group, check if it contains items
        const overGroup = groups.find((g) => g.id === overId);
        if (overGroup) {
          const containerItems = itemsByGroup[overGroup.id] || [];
          if (containerItems.length > 0) {
            // Try to find the closest item within that container using pointerWithin
            // This ensures we only return an item if the pointer is actually over it
            const itemPointerIntersections = pointerWithin({
              ...args,
              droppableContainers: args.droppableContainers.filter(
                (container) => container.id !== overId && containerItems.some((item) => item.id === container.id),
              ),
            });

            if (itemPointerIntersections.length > 0) {
              // Pointer is actually over an item, use it
              const closestItemId = getFirstCollision(itemPointerIntersections, 'id');
              if (closestItemId) {
                overId = closestItemId;
              }
              // else: keep overId as the group container (pointer not over any item)
            }
            // else: pointer is not over any item, keep overId as the group container
          }
          // else: container is empty, keep overId as the group container
        }

        uiState.setLastOverId(overId as string);
        return [{ id: overId }];
      }

      // When a draggable item moves to a new container, the layout may shift
      // and the `overId` may become `null`. We manually set the cached `lastOverId`
      // to the id of the draggable item that was moved to the new container, otherwise
      // the previous `overId` will be returned which can cause items to incorrectly shift positions
      // This also helps when dragging over empty space - we return the last valid drop target
      if (uiState.lastOverId) {
        return [{ id: uiState.lastOverId }];
      }

      return [];
    },
    [uiState, groups, itemsByGroup],
  );

  const handleDragStart = (event: DragStartEvent) => {
    const id = event.active.id as string;
    uiState.setActiveId(id);
    uiState.setLastOverId(null);

    // Check if dragging a group or item
    const isGroup = groups.some((g) => g.id === id);
    if (isGroup) {
      uiState.startDragGroup(id);
    } else {
      uiState.startDrag(id);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) {
      uiState.clearDragOver();
      return;
    }

    const activeId = active.id as string;
    const overId = over.id as string;

    // If dragging a group, no special handling needed (SortableContext handles it)
    if (groups.some((g) => g.id === activeId)) {
      return;
    }

    // Handle item dragging
    const item = items.find((i) => i.id === activeId);
    if (!item) {
      uiState.clearDragOver();
      return;
    }

    // Find the target group - either directly (if over is a group) or via the item's group
    let targetGroupId: string | undefined;
    let isOverItem = false;
    let overItem: ShortlistItem | undefined;

    // Check if dragging over a group directly
    if (groups.some((g) => g.id === overId)) {
      targetGroupId = overId;
    } else {
      // Check if dragging over an item - find which group it belongs to
      overItem = items.find((i) => i.id === overId);
      if (overItem?.groupId) {
        targetGroupId = overItem.groupId;
        isOverItem = true;
      }
    }

    if (!targetGroupId) {
      uiState.clearDragOver();
      return;
    }

    // Scenario 1: Reordering within same group - no special highlighting needed (SortableContext handles it)
    if (item.groupId === targetGroupId) {
      uiState.clearDragOver();
      return;
    }

    // Scenario 2 & 3: Moving to different group
    // Check if an item with the same identity already exists in the target group (for room, same rate = duplicate)
    if (item.referenceId) {
      const targetGroupItems = itemsByGroup[targetGroupId] || [];
      const duplicateExists = targetGroupItems.some((i) => i.id !== item.id && isSameShortlistEntry(i, item));
      if (duplicateExists) {
        uiState.setInvalidDropGroup(targetGroupId);
        uiState.setDropPreviewIndex(null);
        return;
      }
    }

    // Calculate drop preview index
    const targetGroupItems = itemsByGroup[targetGroupId] || [];
    if (isOverItem && overItem) {
      const targetIndex = targetGroupItems.findIndex((i) => i.id === overItem.id);
      if (targetIndex !== -1) {
        uiState.setDropPreviewIndex({ groupId: targetGroupId, index: targetIndex });
      }
    } else {
      // Dropping on group container - append to end
      uiState.setDropPreviewIndex({ groupId: targetGroupId, index: targetGroupItems.length });
    }

    // Show group highlight (for both dropping on item or group container)
    uiState.setDragOverGroup(targetGroupId);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    const activeId = active.id as string;

    // Capture dropPreviewIndex before clearing it
    const previewIndex = uiState.dropPreviewIndex;

    // Clear all drag state immediately, including drag-over highlights
    uiState.clearDragState();
    uiState.clearDragOver();

    // Handle group reordering
    if (groups.some((g) => g.id === activeId)) {
      if (!over) {
        uiState.endDragGroup();
        return;
      }

      const overId = over.id as string;
      const activeIndex = groups.findIndex((g) => g.id === activeId);
      const overIndex = groups.findIndex((g) => g.id === overId);

      if (activeIndex !== overIndex && activeIndex !== -1 && overIndex !== -1) {
        const reorderedGroups = arrayMove(groups, activeIndex, overIndex);
        const groupIds = reorderedGroups.map((g) => g.id);
        await shortlistState.reorderGroups(groupIds);
      }

      uiState.endDragGroup();
      return;
    }

    // Handle item reordering
    if (!over) {
      uiState.endDrag();
      return;
    }

    const overId = over.id as string;
    const item = items.find((i) => i.id === activeId);
    if (!item) {
      uiState.endDrag();
      return;
    }

    // Find the target group - either directly (if over is a group) or via the item's group
    let targetGroupId: string | undefined;
    let isOverItem = false;
    let overItem: ShortlistItem | undefined;

    // Check if dropped on a group directly
    if (groups.some((g) => g.id === overId)) {
      targetGroupId = overId;
    } else {
      // Check if dropped on an item - find which group it belongs to
      overItem = items.find((i) => i.id === overId);
      if (overItem?.groupId) {
        targetGroupId = overItem.groupId;
        isOverItem = true;
      }
    }

    if (!targetGroupId) {
      uiState.endDrag();
      return;
    }

    // Scenario 1: Reorder within same group
    if (item.groupId === targetGroupId) {
      const groupItems = itemsByGroup[targetGroupId] || [];
      const oldIndex = groupItems.findIndex((i) => i.id === activeId);
      const newIndex = isOverItem && overItem ? groupItems.findIndex((i) => i.id === overItem.id) : oldIndex;

      if (oldIndex !== newIndex && oldIndex !== -1 && newIndex !== -1) {
        const reorderedItems = arrayMove(groupItems, oldIndex, newIndex);
        const itemIds = reorderedItems.map((i) => i.id);
        await shortlistState.reorderItems(targetGroupId, itemIds);
      }
      uiState.endDrag();
      return;
    }

    // Scenario 2 & 3: Moving to different group
    // Check if an item with the same identity already exists in the target group (for room, same rate = duplicate)
    if (item.referenceId) {
      const targetGroupItems = itemsByGroup[targetGroupId] || [];
      const duplicateExists = targetGroupItems.some((i) => i.id !== item.id && isSameShortlistEntry(i, item));
      if (duplicateExists) {
        uiState.endDrag();
        return;
      }
    }

    // Use the preview index if available (calculated during drag over)
    // This handles the case where we're dropping at the end of a list
    if (previewIndex && previewIndex.groupId === targetGroupId) {
      await shortlistState.moveToPosition(activeId, targetGroupId, previewIndex.index);
      uiState.endDrag();
      return;
    }

    // Scenario 2: Move to specific position in different group (dropped on an item)
    if (isOverItem && overItem) {
      const targetGroupItems = itemsByGroup[targetGroupId] || [];
      const targetIndex = targetGroupItems.findIndex((i) => i.id === overItem.id);

      if (targetIndex !== -1) {
        await shortlistState.moveToPosition(activeId, targetGroupId, targetIndex);
        uiState.endDrag();
        return;
      }
    }

    // Scenario 3: Move to end of different group (dropped on group container)
    await shortlistState.moveToGroup(activeId, targetGroupId);
    uiState.endDrag();
  };

  const handleDragCancel = () => {
    uiState.clearDragState();
    uiState.endDrag();
    uiState.endDragGroup();
  };

  const activeItem =
    uiState.activeId && !isSortingContainer ? items.find((item) => item.id === uiState.activeId) : null;
  const activeGroup = uiState.activeId && isSortingContainer ? groups.find((g) => g.id === uiState.activeId) : null;

  const draggedItemGroupId = activeItem?.groupId ?? null;
  const draggedItem = React.useMemo(() => {
    if (!uiState.draggedItemId) return null;
    return items.find((item) => item.id === uiState.draggedItemId) ?? null;
  }, [uiState.draggedItemId, items]);

  const renderGroupDragOverlay = (groupId: string) => {
    const group = groups.find((g) => g.id === groupId);
    if (!group) return null;

    return (
      <div className="opacity-95 shadow-2xl rounded-md bg-background-secondary">
        <ShortlistItemsGroup
          group={group}
          draggedItemId={null}
          draggedItemGroupId={null}
          dropPreviewIndex={-1}
          draggedItem={null}
        />
      </div>
    );
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetectionStrategy}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="flex-1 w-full overflow-x-hidden @lg:h-full">
        <div className="flex flex-col gap-1 @lg:flex-row @lg:gap-4 @lg:overflow-y-hidden @lg:overflow-x-auto h-full p-2 pt-0 md:p-4 md:pt-0">
          <SortableContext
            items={groups.map((g) => g.id)}
            strategy={tripsState.isMobile ? verticalListSortingStrategy : horizontalListSortingStrategy}
          >
            {/* Groups */}
            {groups.map((group) => {
              const previewIndex = uiState.dropPreviewIndex?.groupId === group.id ? uiState.dropPreviewIndex.index : -1;

              return (
                <ShortlistItemsGroup
                  key={group.id}
                  group={group}
                  draggedItemId={uiState.draggedItemId}
                  draggedItemGroupId={draggedItemGroupId}
                  dropPreviewIndex={previewIndex}
                  draggedItem={draggedItem}
                />
              );
            })}
          </SortableContext>
        </div>
      </div>
      <DragOverlay adjustScale={false} modifiers={[snapCenterToCursor]}>
        {activeGroup ? (
          renderGroupDragOverlay(activeGroup.id)
        ) : activeItem ? (
          <div className="opacity-95 shadow-2xl rounded-md">
            <ShortlistItemCard item={activeItem} groups={groups} isDragging={true} />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
